import { useEffect } from 'react';

const TermsOfService = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <title>Terms of Service - Veridian Vanguard Advisors</title>
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href={`${siteUrl}/terms-of-service`} />

      <div className="bg-slate-900 text-white min-h-screen font-sans">
        {/* Navigation */}
        <nav className="bg-slate-800/95 backdrop-blur-sm shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center space-x-4">
                <span className="text-2xl font-light tracking-wide">VERIDIAN VANGUARD</span>
              </a>
              <a 
                href="/"
                className="bg-lime-400 text-slate-800 px-6 py-2 rounded-lg font-medium hover:bg-lime-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </a>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="space-y-12">
            <div>
              <h1 className="text-5xl font-extralight mb-4">
                TERMS OF <span className="text-lime-400">SERVICE</span>
              </h1>
              <p className="text-white/60 text-sm">Last Updated: January 27, 2025</p>
            </div>

            <div className="space-y-8 text-white/80 leading-relaxed">
              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">1. Acceptance of Terms</h2>
                <p>
                  Welcome to Veridian Vanguard Advisors. By accessing our website, engaging our advisory services, 
                  or communicating with our team, you agree to be bound by these Terms of Service ("Terms"), our 
                  Privacy Policy, and all applicable laws and regulations.
                </p>
                <p>
                  If you do not agree with any part of these Terms, you must not use our website or services. 
                  These Terms constitute a legally binding agreement between you ("Client," "you," or "your") and 
                  Veridian Vanguard Advisors ("we," "us," or "our").
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">2. Services Overview</h2>
                <p>
                  Veridian Vanguard Advisors provides strategic advisory services to governments, ultra-high-net-worth 
                  individuals, elite athletes, institutions, family offices, philanthropists, business leaders, and 
                  cultural icons. Our services include but are not limited to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Strategic planning and business advisory</li>
                  <li>Market intelligence and competitive analysis</li>
                  <li>Partnership development and relationship management</li>
                  <li>Crisis management and reputation advisory</li>
                  <li>Investment strategy guidance (non-regulated advisory)</li>
                  <li>Philanthropic and legacy planning support</li>
                  <li>Sports and entertainment career advisory</li>
                </ul>
                <p className="mt-4">
                  The specific scope, deliverables, fees, and terms of each engagement will be detailed in a 
                  separate written agreement ("Engagement Agreement") executed between the parties.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">3. Engagement Process</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">3.1 Initial Consultation</h3>
                <p>
                  All prospective client relationships begin with an initial consultation to assess fit, discuss 
                  objectives, and determine whether we can provide value. This consultation does not create a 
                  client-advisor relationship until a formal Engagement Agreement is executed.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">3.2 Due Diligence and Onboarding</h3>
                <p>
                  Prior to engagement, we conduct comprehensive due diligence on all prospective clients, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Identity verification and background checks</li>
                  <li>Source of funds and wealth verification</li>
                  <li>Conflict of interest assessment</li>
                  <li>Compliance screening (AML/KYC/sanctions)</li>
                  <li>Reputation and risk evaluation</li>
                </ul>
                <p className="mt-4">
                  We reserve the right to decline any engagement at our sole discretion, including if we are unable 
                  to complete satisfactory due diligence or identify potential conflicts or compliance concerns.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">3.3 Engagement Agreement</h3>
                <p>
                  Upon successful completion of due diligence, we will provide a detailed Engagement Agreement 
                  outlining:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Scope of services and deliverables</li>
                  <li>Fees, payment terms, and expenses</li>
                  <li>Duration and termination provisions</li>
                  <li>Confidentiality and non-disclosure obligations</li>
                  <li>Intellectual property rights</li>
                  <li>Limitation of liability and indemnification</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">4. Fees and Payment Terms</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">4.1 Fee Structure</h3>
                <p>
                  Our advisory services are provided on a fee basis as specified in each Engagement Agreement. 
                  Fee structures may include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Retainer Fees:</strong> Monthly or annual retainer for ongoing advisory services</li>
                  <li><strong>Project-Based Fees:</strong> Fixed fees for specific projects or deliverables</li>
                  <li><strong>Hourly Rates:</strong> Time-based billing for certain advisory services</li>
                  <li><strong>Success Fees:</strong> Performance-based compensation tied to specific outcomes</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">4.2 Payment Terms</h3>
                <p>
                  Unless otherwise specified in the Engagement Agreement:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Retainer fees are due in advance on a monthly or annual basis</li>
                  <li>Project fees may require an upfront deposit (typically 50%)</li>
                  <li>Invoices are payable within 15 days of receipt</li>
                  <li>Late payments may incur interest charges at 1.5% per month</li>
                  <li>We accept wire transfers, ACH, and other agreed payment methods</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">4.3 Expenses</h3>
                <p>
                  Reasonable out-of-pocket expenses incurred in connection with services (travel, accommodation, 
                  research materials, etc.) will be billed separately unless included in the agreed fee structure.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">4.4 Non-Payment and Suspension</h3>
                <p>
                  We reserve the right to suspend services if payment obligations are not met. Non-payment does not 
                  relieve you of confidentiality obligations or other continuing obligations under the Engagement Agreement.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">5. Client Responsibilities</h2>
                <p>
                  To enable us to provide effective advisory services, clients agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate, complete, and timely information</li>
                  <li>Grant reasonable access to relevant personnel and documents</li>
                  <li>Respond promptly to requests for information or decisions</li>
                  <li>Maintain confidentiality of our proprietary methodologies and insights</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Notify us immediately of any material changes affecting the engagement</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">6. Confidentiality</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">6.1 Mutual Confidentiality</h3>
                <p>
                  Both parties agree to maintain strict confidentiality regarding all proprietary and confidential 
                  information disclosed during the engagement. This includes:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Business strategies, financial information, and trade secrets</li>
                  <li>Personal information and private matters</li>
                  <li>The existence and terms of the advisory relationship</li>
                  <li>All communications and work product</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">6.2 Exceptions</h3>
                <p>
                  Confidentiality obligations do not apply to information that:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Is publicly available through no breach of these Terms</li>
                  <li>Was known prior to disclosure</li>
                  <li>Is independently developed without use of confidential information</li>
                  <li>Must be disclosed pursuant to legal process or regulatory requirement</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">6.3 Duration</h3>
                <p>
                  Confidentiality obligations survive termination of the engagement and continue indefinitely 
                  unless otherwise specified in the Engagement Agreement.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">7. Intellectual Property</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">7.1 Our Intellectual Property</h3>
                <p>
                  All methodologies, frameworks, templates, tools, and proprietary processes used in delivering 
                  our services remain our exclusive intellectual property. Clients receive a limited license to 
                  use deliverables for their internal business purposes only.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">7.2 Client Intellectual Property</h3>
                <p>
                  Clients retain all rights to their pre-existing intellectual property. We may use client 
                  information solely for the purpose of providing advisory services.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">7.3 Work Product</h3>
                <p>
                  Ownership of work product created specifically for a client engagement will be addressed in 
                  the Engagement Agreement. Generally, clients receive ownership of final deliverables while 
                  we retain rights to underlying methodologies and reusable components.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">8. Limitation of Liability</h2>
                <div className="bg-lime-400/10 border-l-4 border-lime-400 p-6 rounded-r-lg">
                  <p className="font-medium text-white mb-3">Important Legal Notice</p>
                  <p>
                    To the fullest extent permitted by applicable law, Veridian Vanguard Advisors' total liability 
                    for any claims arising from or related to our services shall not exceed the fees paid by the 
                    client for the specific engagement giving rise to the claim.
                  </p>
                </div>
                <p className="mt-4">
                  We shall not be liable for any indirect, incidental, consequential, special, or punitive damages, 
                  including but not limited to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Lost profits or business opportunities</li>
                  <li>Loss of reputation or goodwill</li>
                  <li>Business interruption</li>
                  <li>Loss of data or information</li>
                  <li>Third-party claims</li>
                </ul>
                <p className="mt-4">
                  This limitation applies regardless of the legal theory (contract, tort, negligence, strict 
                  liability, or otherwise) and even if we have been advised of the possibility of such damages.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">9. Indemnification</h2>
                <p>
                  You agree to indemnify, defend, and hold harmless Veridian Vanguard Advisors, its principals, 
                  employees, and affiliates from any claims, damages, losses, liabilities, and expenses (including 
                  reasonable attorneys' fees) arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your breach of these Terms or any Engagement Agreement</li>
                  <li>Your violation of any law or regulation</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Your misuse of our advice or recommendations</li>
                  <li>Inaccurate or incomplete information you provide</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">10. Termination</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">10.1 Termination by Either Party</h3>
                <p>
                  Either party may terminate an engagement as specified in the Engagement Agreement, typically 
                  with 30 days' written notice. Immediate termination may be permitted in cases of material breach.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">10.2 Effect of Termination</h3>
                <p>
                  Upon termination:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Client remains obligated to pay for services rendered through the termination date</li>
                  <li>We will deliver all completed work product</li>
                  <li>Confidentiality obligations continue indefinitely</li>
                  <li>Both parties will return or destroy confidential information as requested</li>
                  <li>Provisions regarding limitation of liability, indemnification, and dispute resolution survive</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">10.3 No Refunds</h3>
                <p>
                  Fees paid for services already rendered are non-refundable. Retainer fees may be prorated 
                  based on the termination date as specified in the Engagement Agreement.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">11. Conflicts of Interest</h2>
                <p>
                  We maintain strict policies to identify and manage conflicts of interest. We will disclose any 
                  actual or potential conflicts and obtain your informed consent before proceeding with an engagement.
                </p>
                <p>
                  We may provide services to multiple clients in the same industry or with competing interests, 
                  provided we maintain appropriate information barriers and confidentiality safeguards. If an 
                  unmanageable conflict arises during an engagement, we may be required to withdraw from representation.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">12. Website Use and Restrictions</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">12.1 Permitted Use</h3>
                <p>
                  You may access and use our website for informational purposes and to inquire about our services. 
                  You agree not to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use automated systems to access or scrape website content</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with website functionality or security</li>
                  <li>Use the website for any unlawful purpose</li>
                  <li>Reproduce, distribute, or create derivative works without permission</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">12.2 Website Availability</h3>
                <p>
                  We strive to maintain website availability but do not guarantee uninterrupted access. We may 
                  modify, suspend, or discontinue any aspect of the website at any time without notice.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">13. Dispute Resolution</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">13.1 Informal Resolution</h3>
                <p>
                  In the event of any dispute, both parties agree to first attempt resolution through good-faith 
                  negotiation. Either party may initiate this process by providing written notice of the dispute.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">13.2 Mediation</h3>
                <p>
                  If informal negotiation does not resolve the dispute within 30 days, the parties agree to 
                  participate in confidential mediation before a mutually agreed mediator.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">13.3 Arbitration</h3>
                <p>
                  If mediation is unsuccessful, disputes shall be resolved through binding arbitration in accordance 
                  with the rules of the American Arbitration Association. The arbitration shall be conducted in 
                  Los Angeles, California, unless otherwise agreed.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">13.4 Exceptions</h3>
                <p>
                  Either party may seek injunctive relief in court to protect intellectual property rights or 
                  enforce confidentiality obligations without first pursuing arbitration.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">14. Governing Law</h2>
                <p>
                  These Terms and any Engagement Agreements shall be governed by and construed in accordance with 
                  the laws of the State of California, United States, without regard to its conflict of law provisions.
                </p>
                <p>
                  For international clients, specific governing law provisions may be addressed in individual 
                  Engagement Agreements to reflect the nature of the engagement and applicable jurisdictions.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">15. Modifications to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. Material changes will be communicated 
                  via email or website notice. Continued use of our website or services after changes constitutes 
                  acceptance of the modified Terms.
                </p>
                <p>
                  Modifications do not apply retroactively to existing Engagement Agreements unless mutually agreed 
                  in writing.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">16. Severability</h2>
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be 
                  limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain 
                  in full force and effect.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">17. Entire Agreement</h2>
                <p>
                  These Terms, together with any executed Engagement Agreement and our Privacy Policy, constitute 
                  the entire agreement between you and Veridian Vanguard Advisors regarding use of our website and 
                  services, superseding any prior agreements or understandings.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">18. Contact Information</h2>
                <p>
                  For questions regarding these Terms of Service, please contact:
                </p>
                <div className="bg-white/5 p-6 rounded-lg border border-lime-400/20 mt-4">
                  <p><strong>Veridian Vanguard Advisors</strong></p>
                  <p>Legal Department</p>
                  <p>Email: connect@veridianvanguard.com</p>
                  <p>Location: Los Angeles, CA</p>
                </div>
              </section>

              <div className="bg-lime-400/10 border border-lime-400/30 p-6 rounded-lg mt-8">
                <p className="text-white font-medium">
                  By using our website or engaging our services, you acknowledge that you have read, understood, 
                  and agree to be bound by these Terms of Service.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black py-12 mt-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center space-x-8 text-sm">
                <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors underline">Privacy Policy</a>
                <a href="/terms-of-service" className="text-lime-400 hover:text-lime-300 transition-colors underline">Terms of Service</a>
                <a href="/disclaimers" className="text-gray-400 hover:text-white transition-colors underline">Disclaimers</a>
                <a href="https://readdy.ai/?ref=logo" className="text-gray-400 hover:text-lime-400 transition-colors underline">Powered by Readdy</a>
              </div>
              <div className="text-xs text-gray-500">
                © 2025 Veridian Vanguard Advisors. All Rights Reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsOfService;
